import React, { useCallback, useMemo, useRef } from 'react';
import Avatar from '../../components/Avatar';
import './overlaybutton.css';
import { sourceSet } from '../../helper';
import useFocusTrap from '../../hooks/useFocusTrap';
import useOverlayVideo from '../../hooks/useOverlayVideo';

function OverlayButton(props) {
	const triggerRef = useRef(null);
	const videoRef = useRef(null);
	const videoLeftRef = useRef(null);
	const videoRightRef = useRef(null);
	const closeButtonRef = useRef(null);
	const soundButtonRef = useRef(null);

	const {
		opened,
		progressVal,
		left,
		top,
		isMuted,
		containerWidth,
		containerMarginLeft,
		openOverlay,
		closeOverlay,
		toggleSound
	} = useOverlayVideo({
		triggerRef,
		videoRef,
		videoLeftRef,
		videoRightRef
	});

	const handleEscape = useCallback(() => {
		closeOverlay();
		if (triggerRef.current) {
			triggerRef.current.focus();
		}
	}, [closeOverlay]);

	useFocusTrap(opened, [closeButtonRef, soundButtonRef], handleEscape);

	const soundIcon = useMemo(() => {
		if (!isMuted) {
			return <path fill="#000" d="M3 9v6h4l5 5V4L7 9H3zm7-.17v6.34L7.83 13H5v-2h2.83L10 8.83zM16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77 0-4.28-2.99-7.86-7-8.77z"/>;
		}

		return <path fill="#000" d="M4.34 2.93L2.93 4.34 7.29 8.7 7 9H3v6h4l5 5v-6.59l4.18 4.18c-.65.49-1.38.88-2.18 1.11v2.06c1.34-.3 2.57-.92 3.61-1.75l2.05 2.05 1.41-1.41L4.34 2.93zM10 15.17L7.83 13H5v-2h2.83l.88-.88L10 11.41v3.76zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zm-7-8l-1.88 1.88L12 7.76zm4.5 8c0-1.77-1.02-3.29-2.5-4.03v1.79l2.48 2.48c.01-.08.02-.16.02-.24z"/>;
	}, [isMuted]);

	return (
		<div>
			<button
				ref={triggerRef}
				className="overlay-trigger"
				id="overlay-trigger--avatar"
				type="button"
				aria-label="Open introduction video overlay"
				onClick={openOverlay}
			>
				<Avatar
					alt={props.alt}
					avatar={props.avatar}
					avatarSrcSet={props.avatarSrcSet}
				/>
			</button>
			<div className="overlay">
				<div className="overlay-container" style={{ left, top, maxWidth: containerWidth, marginLeft: containerMarginLeft }}>
					<div className="video-container lh-1">
						<div className="video-ui video-ui--row-top t-8">
							<progress id="progress" max="100" value={progressVal}>Progress</progress>
							<div className="video-avatar" onClick={closeOverlay}>
								<img
									src={opened ? props.avatar : 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=='}
									srcSet={opened ? sourceSet(props.avatarSrcSet) : ''}
									sizes="32px"
									alt={props.alt}
									width="32"
									height="32"
									className="br-50 mr-0-5"
								/>
								<div className="fw-600">{props.name}</div>
							</div>
							<button
								ref={closeButtonRef}
								className="video-icon--close"
								id="overlay-close-button"
								type="button"
								aria-label="Close video overlay"
								onClick={closeOverlay}
							>
								<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
									<path fill="white" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
								</svg>
							</button>
						</div>
						<div className="video-ui video-ui--row-bottom">
							<button
								ref={soundButtonRef}
								className="video-btn-sound"
								id="overlay-sound-button"
								type="button"
								aria-label={isMuted ? 'Turn video sound on' : 'Turn video sound off'}
								aria-pressed={!isMuted}
								onClick={toggleSound}
							>
								<svg className="svg-sound" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
									{soundIcon}
								</svg>
								{isMuted ? <span>TURN SOUND ON</span> : null}
							</button>
						</div>
						<div ref={videoLeftRef} className="video-ui--view-50 l-0" id="video-left"></div>
						<div ref={videoRightRef} className="video-ui--view-50 r-0" id="video-right"></div>
						<video
							ref={videoRef}
							type="video/mp4"
							src={props.video}
							id="video-intro"
							playsInline
							muted
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

export default OverlayButton;
